import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";
import { formatToCustomLocale } from "../../../utilities";

/**
 * LineWinTextController - Manages line win text with spine animation
 * Uses winline_digit spine with txt_winline_value bone for text attachment
 * Replaces anime.js animations with native spine animations
 */
class LineWinTextController {
  constructor(container, symbolWidth, symbolHeight) {
    this.container = container;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    // Spine instance
    this.winlineSpine = null;
    this.initialized = false;
    this.isCleanedUp = false;

    // Text container attached to spine bone
    this.textContainer = null;
    this.textBitmap = null;
    this.boneUpdateTicker = null;

    this.initializeSpine();
  }

  /**
   * Check if controller is ready
   */
  isReady() {
    return this.winlineSpine?.state && this.initialized && !this.isCleanedUp;
  }

  /**
   * Initialize the winline_digit spine
   */
  initializeSpine() {
    try {
      const assets = PIXI.Assets.cache;
      if (
        !assets.has("winline_digit-skeleton") ||
        !assets.has("winline_digit-atlas")
      ) {
        return;
      }

      const skeletonData = PIXI.Assets.get("winline_digit-skeleton");
      const atlasData = PIXI.Assets.get("winline_digit-atlas");

      if (!skeletonData || !atlasData) {
        return;
      }

      this.winlineSpine = Spine.from({
        skeleton: "winline_digit-skeleton",
        atlas: "winline_digit-atlas",
      });

      if (
        !this.winlineSpine ||
        !this.winlineSpine.skeleton ||
        !this.winlineSpine.state
      ) {
        return;
      }

      // Scale spine to match symbol size
      this.applySpineScaling();

      // Position at center
      this.winlineSpine.x = 0;
      this.winlineSpine.y = 0;
      this.winlineSpine.zIndex = 20;
      this.winlineSpine.alpha = 0.001; // Hidden initially, keep in render pipeline

      this.container.addChild(this.winlineSpine);

      // Initialize text attached to bone
      this.initializeText();

      this.initialized = true;

      // Warm up spine to prevent first-use jitter
      this.warmUpSpine();
    } catch (error) {
      console.error("[LineWinTextController] Error initializing spine:", error);
    }
  }

  /**
   * Warm up spine by setting animation and updating transforms
   * This ensures spine is fully ready before first use (prevents jitter)
   */
  warmUpSpine() {
    if (!this.winlineSpine?.state) return;

    try {
      // Set the animation while invisible
      this.winlineSpine.state.setAnimation(0, "winline_txt", false);
      // Update state with a tiny delta to populate spine structure
      this.winlineSpine.state.update(0.016);
      // Update world transform to ensure everything is calculated
      try {
        if (this.winlineSpine.skeleton.physics !== undefined) {
          this.winlineSpine.skeleton.updateWorldTransform();
        }
      } catch (transformError) {
        // Skip updateWorldTransform if physics is not initialized
      }

      // NOW sync text position with bone (after warmup)
      if (this.textContainer) {
        this.updateTextFromBone();
      }

      // DON'T clear tracks - keep bone at animation frame 0 for first use
    } catch (error) {
      // Silent fail - warmup is optional optimization
    }
  }

  /**
   * Apply scaling to match symbol size
   */
  applySpineScaling() {
    if (!this.winlineSpine) return;

    // Scale to match symbol size (same pattern as OverlayReelController)
    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;
    const widthScale = this.symbolWidth / nativeWidth;
    const heightScale = this.symbolHeight / nativeHeight;
    const scaleRatio = Math.min(widthScale, heightScale);

    this.winlineSpine.scale.set(scaleRatio);
  }

  /**
   * Initialize text container and attach to spine bone
   */
  initializeText() {
    if (!this.winlineSpine || !this.winlineSpine.skeleton) {
      return;
    }

    // Verify bone exists
    const textBone = this.winlineSpine.skeleton.findBone("txt_winline_value");
    if (!textBone) {
      console.warn(
        "[LineWinTextController] txt_winline_value bone not found in spine"
      );
      return;
    }

    // Create text container as child of spine
    this.textContainer = new PIXI.Container();
    this.textContainer.name = "winline_text_attached_to_bone";
    this.textContainer.alpha = 0.001;
    this.textContainer.zIndex = 1000;

    this.winlineSpine.addChild(this.textContainer);
    this.winlineSpine.sortableChildren = true;

    // Create BitmapText once during initialization
    // Use same formula as coin value text: 25% of NATIVE symbol height (Settings.SYMBOL_HEIGHT)
    // Text scales automatically because it's a child of the spine
    const fontSize = Math.floor(Settings.SYMBOL_HEIGHT * 0.25);

    this.textBitmap = new PIXI.BitmapText("", {
      fontName: "font",
      fontSize: fontSize,
    });

    this.textBitmap.anchor.set(0.5);
    this.textBitmap.x = 0;
    this.textBitmap.y = 0;

    this.textContainer.addChild(this.textBitmap);
  }

  /**
   * Update text display with new value
   */
  updateTextDisplay(value) {
    if (!this.textBitmap) return;

    // Format value with currency
    const formattedValue = formatToCustomLocale(Math.floor(value));
    const textWithCurrency = `${formattedValue}$`;

    // Simply update the text value (BitmapText already created)
    this.textBitmap.text = textWithCurrency;
  }

  /**
   * Sync text container transform with spine's txt_winline_value bone
   * Called every frame while spine is animating
   */
  updateTextFromBone() {
    if (
      !this.winlineSpine ||
      !this.textContainer ||
      !this.winlineSpine.skeleton
    ) {
      return;
    }

    const textBone = this.winlineSpine.skeleton.findBone("txt_winline_value");
    if (!textBone) {
      return;
    }

    // Apply bone's world transform to text container
    this.textContainer.x = textBone.worldX;
    this.textContainer.y = textBone.worldY;
    this.textContainer.rotation =
      (textBone.getWorldRotationX() * Math.PI) / 180;
    this.textContainer.scale.set(
      textBone.getWorldScaleX(),
      textBone.getWorldScaleY()
    );
  }

  /**
   * Start syncing text with spine bone every frame
   */
  startBoneUpdateLoop() {
    if (!this.winlineSpine || !this.textContainer) return;

    if (!this.boneUpdateTicker) {
      this.boneUpdateTicker = () => {
        if (this.winlineSpine && this.winlineSpine.alpha > 0.01) {
          this.updateTextFromBone();
        }
      };
      PIXI.Ticker.shared.add(this.boneUpdateTicker);
    }
  }

  /**
   * Stop syncing text with spine bone
   */
  stopBoneUpdateLoop() {
    if (this.boneUpdateTicker) {
      PIXI.Ticker.shared.remove(this.boneUpdateTicker);
      this.boneUpdateTicker = null;
    }
  }

  /**
   * Show win amount with spine animation
   */
  showWinAmount(amount) {
    if (!this.isReady()) return;

    // Update text value FIRST (while invisible)
    this.updateTextDisplay(amount);

    // Set animation WHILE INVISIBLE to prepare it (this clears existing tracks)
    // Play once (not looping) - animation will complete naturally
    if (this.winlineSpine.state) {
      this.winlineSpine.state.setAnimation(0, "winline_txt", false);
    }

    // Sync text position with bone (bone will be at animation start automatically)
    this.updateTextFromBone();

    // Use requestAnimationFrame to ensure spine updates before becoming visible
    // This prevents any flash or visual artifacts
    requestAnimationFrame(() => {
      if (!this.winlineSpine || this.isCleanedUp) return;

      // NOW make visible after spine has had a chance to update
      this.winlineSpine.alpha = 1;
      this.textContainer.alpha = 1;

      // Make BitmapText visible
      if (this.bitmapText) {
        this.bitmapText.alpha = 1;
        this.bitmapText.visible = true;
      }

      // Start bone update loop
      this.startBoneUpdateLoop();
    });
  }

  /**
   * Hide the win text - forceful cleanup to prevent stuck animations
   */
  hide() {
    if (!this.winlineSpine) return;

    // Stop bone update loop immediately
    this.stopBoneUpdateLoop();

    // Hide spine and text immediately (keep at 0.001 to maintain render loop and prevent flickering)
    this.winlineSpine.alpha = 0.001;
    if (this.textContainer) {
      this.textContainer.alpha = 0.001;
    }

    // Explicitly hide the BitmapText to prevent visible dots
    if (this.bitmapText) {
      this.bitmapText.alpha = 0;
      this.bitmapText.visible = false;
    }

    // Clear animation tracks and listeners to prevent stuck animations
    if (this.winlineSpine.state) {
      this.winlineSpine.state.clearTracks();
      this.winlineSpine.state.clearListeners();
    }

    // Re-warm the spine to prepare it for next use (same as initial warmup)
    // This ensures it's in the same state as the first time
    requestAnimationFrame(() => {
      if (!this.winlineSpine?.state || this.isCleanedUp) return;

      // Only re-warm if spine is still hidden (alpha is 0.001)
      // If it's been shown again, don't interfere with the new animation
      if (this.winlineSpine.alpha > 0.01) return;

      try {
        // Set the animation while invisible (same as warmup)
        this.winlineSpine.state.setAnimation(0, "winline_txt", false);
        // Update state with a tiny delta to populate spine structure
        this.winlineSpine.state.update(0.016);
        // Update world transform to ensure everything is calculated
        try {
          if (this.winlineSpine.skeleton.physics !== undefined) {
            this.winlineSpine.skeleton.updateWorldTransform();
          }
        } catch (transformError) {
          // Skip updateWorldTransform if physics is not initialized
        }

        // Sync text position with bone (after warmup)
        if (this.textContainer) {
          this.updateTextFromBone();
        }

        // DON'T clear tracks - keep bone at animation frame 0 for next use
      } catch (error) {
        // Silent fail - warmup is optional optimization
      }
    });
  }

  /**
   * Update dimensions when symbol size changes
   */
  updateDimensions(newSymbolWidth, newSymbolHeight) {
    if (
      this.symbolWidth === newSymbolWidth &&
      this.symbolHeight === newSymbolHeight
    ) {
      return;
    }

    this.symbolWidth = newSymbolWidth;
    this.symbolHeight = newSymbolHeight;

    // Only need to scale the spine - BitmapText scales automatically as a child of the spine
    // Font size is based on Settings.SYMBOL_HEIGHT (native), so it doesn't need to change
    if (this.winlineSpine) {
      this.applySpineScaling();
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.isCleanedUp = true;

    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Cleanup text
    if (this.textBitmap) {
      this.textBitmap.destroy();
      this.textBitmap = null;
    }

    if (this.textContainer) {
      if (this.textContainer.parent) {
        this.textContainer.parent.removeChild(this.textContainer);
      }
      this.textContainer.destroy({ children: true });
      this.textContainer = null;
    }

    // Cleanup spine
    if (this.winlineSpine && !this.winlineSpine.destroyed) {
      try {
        if (this.winlineSpine.state) {
          this.winlineSpine.state.clearTracks();
          this.winlineSpine.state.clearListeners();
        }
        if (this.winlineSpine.parent) {
          this.winlineSpine.parent.removeChild(this.winlineSpine);
        }
        this.winlineSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Silent fail
      }
    }

    this.winlineSpine = null;
    this.container = null;
    this.initialized = false;
  }
}

export default LineWinTextController;
